// Boundary between the Monitoring Event Trace API and the Event Trace screen.
// List:   GET {domain}monitoring/event-trace/?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD&page=N&page_size=N
// Detail: GET {domain}monitoring/event-trace/<id>/
//
// <id> is the record's own `id` from the list row. It is not the
// `global_event_id`: that is the number shown to the user ("Event #1021"),
// while the URL takes the record id (e.g. .../event-trace/1/).

export const eventTraceListUrl = (domain, page, startDate, endDate, pageSize = 25) =>
  `${domain}monitoring/event-trace/?${new URLSearchParams({ start_date: startDate, end_date: endDate, page: page ?? 1, page_size: pageSize })}`;

export const eventTraceDetailUrl = (domain, id) =>
  `${domain}monitoring/event-trace/${encodeURIComponent(id)}/`;

const text = (value, fallback = "—") =>
  value === null || value === undefined || value === "" ? fallback : String(value);

// A mobile punch is captured either Online or Offline (`capture_context`);
// show it beside the source, e.g. "Mobile (Offline)". Other sources, or a
// mobile punch without the value, show the source alone.
const sourceWithCaptureContext = (source, captureContext) => {
  const label = text(source);
  const context = text(captureContext, "");
  return String(source ?? "").trim().toLowerCase() === "mobile" && context
    ? `${label} (${context})`
    : label;
};

// One row of the list response. `employee` is the plain text behind the
// Employee column so search, filters and exports match what is displayed.
export const mapEventRow = (row = {}) => {
  const name = text(row.employee_name, "Unknown");
  const employeeId = text(row.employee_code, "");
  return {
    // Record id: the detail URL is built from this. A list row without one
    // falls back to global_event_id so the row can still be selected.
    id: row.id ?? row.global_event_id,
    globalEventId: row.global_event_id ?? null,
    name,
    employeeId,
    employee: employeeId ? `${name} (${employeeId})` : name,
    device: text(row.device_name),
    deviceId: text(row.device_id, ""),
    source: text(row.source),
    direction: text(row.direction),
    eventTime: row.time || null,
    stageName: text(row.current_stage),
    status: text(row.status),
    action: row.action || ""
  };
};

export const mapEventList = (response) => {
  const data = response || {};
  const results = Array.isArray(data.results) ? data.results : [];
  return {
    count: Number(data.count) || results.length,
    hasNext: Boolean(data.next),
    rows: results.map(mapEventRow)
  };
};

// The reason arrives as { title, detail }; a plain string is accepted too.
const mapReason = (reason) => {
  if (!reason) return null;
  if (typeof reason === "string") return { title: "Reason", detail: reason };
  if (!reason.title && !reason.detail) return null;
  return { title: reason.title || "Reason", detail: reason.detail || "" };
};

export const mapEventDetail = (response) => {
  const data = response || {};
  const name = text(data.employee_name, "Unknown");
  return {
    id: data.id ?? data.global_event_id,
    globalEventId: data.global_event_id ?? null,
    name,
    employeeId: text(data.employee_code, ""),
    device: text(data.device_name),
    deviceId: text(data.device_id, ""),
    location: text(data.location_name),
    source: sourceWithCaptureContext(data.source, data.capture_context),
    direction: text(data.direction),
    eventTime: data.event_time || null,
    receivedTime: data.received_time || null,
    stageName: text(data.current_stage),
    status: text(data.status),
    reason: mapReason(data.reason),
    journey: (Array.isArray(data.processing_journey) ? data.processing_journey : [])
      .map((step, index) => ({
        step: step.step ?? index + 1,
        name: text(step.name),
        status: text(step.status, "Pending"),
        time: step.time || null,
        message: step.message || ""
      }))
      .sort((a, b) => a.step - b.step),
    actions: Array.isArray(data.actions) ? data.actions.filter(Boolean) : [],
    punchEntryId: data.punch_entry_id ?? null,
    attendanceRecordId: data.attendance_record_id ?? null
  };
};
